import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASelect, ASwitch } from '../../../components';
import { Form } from 'antd';
import {
  IClient,
  IForm,
  IModelForm,
  IProfessional,
} from '../../../shared/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { schemaClientForm } from '../schemas/yup.client-form.schema';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

interface IFormProps {
  open: boolean;
  close: () => void;
  clientForm: IForm | null;
  setClientForms: React.Dispatch<React.SetStateAction<IForm[]>>;
  professionals: IProfessional[];
  clients: IClient[];
  modelForms: IModelForm[];
}

const FormClientCreateEdit: React.FC<IFormProps> = ({
  open,
  close,
  clientForm,
  setClientForms,
  professionals,
  clients,
  modelForms,
}) => {
  const { addToast } = useToast();
  const [client_id, setClient_id] = useState<string>();
  const [professional_id, setProfessional_id] = useState<string>();
  const [form_id, setForm_id] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(() => {
    schemaClientForm
      .validate({
        client_id,
        professional_id,
        form_id,
      })
      .then(async (validated) => {
        await api
          .post(`/clients-forms/`, validated)
          .then((response) => {
            setClientForms((state: IForm[]) => [...state, response.data]);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Cadastrado com sucesso.',
            });
            close();
          })
          .catch((err) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: err.response.data.message,
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [client_id, professional_id, form_id]);

  const handleEdit = useCallback(() => {
    schemaClientForm
      .validate({
        client_id,
        professional_id,
        form_id,
        status: !!status,
      })
      .then(async (validated) => {
        await api
          .patch(`/clients-forms/${clientForm?.id}`, validated)
          .then((response) => {
            setClientForms((state: IForm[]) =>
              state.map((e) => {
                if (e.id !== response.data.id) {
                  return e;
                }
                e = response.data;
                return e;
              }),
            );
            addToast({
              type: 'info',
              title: 'Alterado',
              description: 'Dados alterados com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.message,
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [client_id, professional_id, form_id, status]);

  const renderStatusForm = () => {
    if (clientForm) {
      return (
        <Form.Item label="Ativo">
          <ASwitch
            checked={status}
            checkedChildren="Sim"
            unCheckedChildren="Não"
            onChange={() => {
              setStatus(!status);
            }}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item label="Modelo de Formulário" required>
        <ASelect
          showSearch
          placeholder="Selecione o modelo"
          optionFilterProp="children"
          onChange={(e) => setForm_id(e)}
          filterOption={(input, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          value={form_id}
          allowClear
          options={modelForms.map((mod) => ({
            label: mod.title,
            value: mod.id,
          }))}
        />
      </Form.Item>
    );
  };

  useEffect(() => {
    if (clientForm) {
      setClient_id(clientForm.client_id);
      setProfessional_id(clientForm.professional_id);
      setForm_id(clientForm.form_id);
      setStatus(clientForm.status ?? false);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        clientForm ? (
          <>
            <EditOutlined /> Editar
          </>
        ) : (
          <>
            <FormOutlined /> Cadastrar
          </>
        )
      }
      onOk={clientForm ? handleEdit : handleCreate}
      width={800}
    >
      <Form layout="vertical">
        <Form.Item label="Cliente" required>
          <ASelect
            showSearch
            placeholder="Selecione o cliente"
            optionFilterProp="children"
            onChange={(e) => setClient_id(e)}
            filterOption={(input, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={client_id}
            allowClear
            options={clients.map((client) => ({
              label: client.corporate_name,
              value: client.id,
            }))}
          />
        </Form.Item>
        <Form.Item label="Profissional" required>
          <ASelect
            showSearch
            placeholder="Selecione o profissional"
            optionFilterProp="children"
            onChange={(e) => setProfessional_id(e)}
            filterOption={(input, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={professional_id}
            allowClear
            options={professionals.map((prof) => ({
              label: prof.name,
              value: prof.id,
            }))}
          />
        </Form.Item>
        {renderStatusForm()}
      </Form>
    </AModal>
  );
};

export default FormClientCreateEdit;
